import React, { useEffect, useState } from "react";
import { Typography, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useListSuperAdmins } from "../../hooks/api/useAdminUsers";
import type { SuperAdminUser } from "../../hooks/api/useAdminUsers";
import { getAdminUserDetailPath } from "../../constants/routes";
import { formatRoleForDisplay } from "../../Graphql/types/auth";
import { formatAccountStatusLabel } from "../common/adminEnumLabels";
import AdminPageShell from "../common/AdminPageShell";
import AdminDataTable, {
  type AdminTableColumn,
} from "../common/AdminDataTable";

const AdminUserListPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<SuperAdminUser[]>([]);
  const listSuperAdmins = useListSuperAdmins();

  useEffect(() => {
    listSuperAdmins
      .mutateAsync()
      .then((res) => {
        const list = res.listSuperAdmins?.users ?? [];
        setUsers(list);
      })
      .catch(() => {});
  }, []);

  const isLoading = listSuperAdmins.isPending && users.length === 0;
  const isEmpty = !isLoading && users.length === 0;

  const columns: AdminTableColumn<SuperAdminUser>[] = [
    {
      id: "firstName",
      header: "First name",
      mobilePrimary: true,
      renderCell: (u) => (
        <Typography variant="body2" fontWeight={600}>
          {u.firstName ?? "—"}
        </Typography>
      ),
    },
    {
      id: "lastName",
      header: "Last name",
      renderCell: (u) => u.lastName ?? "—",
    },
    {
      id: "email",
      header: "Email",
      renderCell: (u) => (
        <Typography variant="body2" color="text.secondary">
          {u.email ?? "—"}
        </Typography>
      ),
    },
    {
      id: "role",
      header: "Role",
      renderCell: (u) => (u.role ? formatRoleForDisplay(u.role) : "—"),
    },
    {
      id: "status",
      header: "Status",
      renderCell: (u) =>
        u.status ? (
          <Chip
            size="small"
            label={formatAccountStatusLabel(u.status)}
            color={u.status === "ACTIVE" ? "success" : "default"}
            variant="outlined"
          />
        ) : (
          "—"
        ),
    },
  ];

  return (
    <AdminPageShell
      title="Admin Users"
      description="Primary and secondary super administrators."
      meta={
        isLoading
          ? "Loading…"
          : isEmpty
            ? "No admin users"
            : `${users.length} ${users.length === 1 ? "user" : "users"}`
      }
    >
      <AdminDataTable
        columns={columns}
        rows={users}
        getRowId={(u) => u.id}
        onRowClick={(u) => navigate(getAdminUserDetailPath(u.id))}
        emptyMessage="No admin users found."
        loading={isLoading}
      />
    </AdminPageShell>
  );
};

export default AdminUserListPage;
